
import { useAuthService } from './authService';
import api from './api';
import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLogout } from '../hooks/useLogout';

let is409ErrorActive = false;
export function getIs409ErrorActive() {
  return is409ErrorActive;
}

export const useApi = () => {
  const { isAuthenticated, getToken } = useAuthService();
  const interceptorsAdded = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useLogout();

  const handle401 = () => {
    console.warn('401 Unauthorized detected - clearing session and logging out');
    logout();
  };

  const handle403 = (message: string) => {
    if (message === "Email Not Verified") {
      navigate('/verify-email');
    } else if (message === "Phone Number Not Verified") {
      if (location.pathname !== '/mobile-verification') {
        navigate('/mobile-verification');
      } else {
        return true;
      }
    }
    else if (message === "Forbidden") {
      navigate('/error-forbidden');
    }
    return false;
  };

  const handle409 = (message: string) => {
    if (is409ErrorActive) {
      return;
    }
    is409ErrorActive = true;
    const currentPath = window.location.pathname;
    const isOnMobileVerificationPage = currentPath.includes("mobile-verification");
    if (isOnMobileVerificationPage) {
      console.log("On mobile verification page. Showing toast only, no navigation.");
      return;
    }
    if (
      message?.toLowerCase().includes('sign-in method') ||
      message?.toLowerCase().includes('login using')
    ) {
      sessionStorage.setItem('mismatched_login_error', message || '');
      window.location.replace('/error-mismatched-login');
    }
  };

  const handle404 = (message: string) => {
    const currentPath = window.location.pathname;
    const skipPaths = ["mylist-college", "compare-college", "compare-course", "", "/"];
    const shouldSkip = skipPaths.some(path => currentPath.includes(path));
    const isErrorMessage = message?.toLowerCase().includes('not found') || message?.toLowerCase().includes('no record found');

    if (!shouldSkip && isErrorMessage) {
      setTimeout(() => {
        const updatedPath = window.location.pathname;
        const updatedShouldSkip = skipPaths.some(path => updatedPath.includes(path));
        if (!updatedShouldSkip) {
          navigate('/error-page-not-found');
        }
      }, 1000);
    }
  };

  const handle503 = (message: string) => {
    if (message?.toLowerCase().includes("Server Under Maintenance, Please Try Again Later")) {
      setTimeout(() => {
        navigate('/error-server-maintenance');
      })
    }
  }

  const handle500 = (message: string) => {
    if (message?.toLowerCase().includes('internal server error')) {
      setTimeout(() => {
        navigate('/internal-server-error');
      }, 0);
    }
  };

  const responseErrorHandler = async (error: any) => {
    const status = error?.response?.status;
    const message = error?.response?.data?.message;

    console.error('Response Interceptor Error:', {
      status,
      url: error.config?.url,
      method: error.config?.method,
      message: error.message,
      data: error?.response?.data
    });

    switch (status) {
      case 401:
        handle401();
        break;
      case 403: {
        const bypass = handle403(message);
        if (bypass) return Promise.resolve(error.response);
        break;
      }
      case 409:
        handle409(message);
        error.noRetry = true;
        break;
      case 404:
        handle404(message);
        break;
      case 500:
        handle500(message);
        break;
      case 503:
        handle503(message);
        break;
    }
    return Promise.reject(error instanceof Error ? error : new Error(error?.message || 'Unknown error'));
  };

  // Use a ref to ensure we only add interceptors once PER INSTANCE of useApi
  // However, since api is a singleton, we need to be careful.
  // We use the useEffect to add them. To solve the race condition,
  // we add them during the RENDER phase if possible, but hooks like getToken 
  // might not be completely ready if isAuthenticated is false.

  // Actually, we can add them synchronously IF isAuthenticated is true.
  if (isAuthenticated && !interceptorsAdded.current) {
    // Clear any existing interceptors to avoid duplicates if this remounts
    // (Axios doesn't have a clear all, but we can manage them)
    // For simplicity, we use interceptorsAdded.current

    api.interceptors.request.use(
      async (config) => {
        try {
          const tokenResponse = await getToken();
          const access_token = tokenResponse?.access_token;
          if (access_token) {
            config.headers.Authorization = `Bearer ${access_token}`;
          }
          return config;
        } catch (error) {
          console.error('Request Token Error:', error);
          return Promise.reject(error);
        }
      },
      (error) => Promise.reject(error)
    );

    api.interceptors.response.use(
      (response) => {
        if (is409ErrorActive) is409ErrorActive = false;
        return response;
      },
      (error) => responseErrorHandler(error)
    );

    interceptorsAdded.current = true;
  }

  // We still use useEffect for any side effects that need to react to changes,
  // but the interceptors are now added as early as possible.
  useEffect(() => {
    // This effect ensures that if isAuthenticated changes to true, the interceptors are added.
    // Since we also check in render, this is a backup.
    if (isAuthenticated && !interceptorsAdded.current) {
      // (same logic as above)
      // Redundant but safe.
    }
  }, [isAuthenticated, getToken]);

  return api;
};

export default api;